// Mind2Magic CRM — Learning System (Magic Planner 1.5)
// Tracks Sara's edits between generated and uploaded DOCX versions.
// Detects patterns over time to improve future itinerary generation.

import { store } from '../store.js'

// ── TRACK CHANGES ────────────────────────────────────────────────────────
// Compare original generated sections vs edited sections from uploaded DOCX

export function trackChanges(originalSections, editedSections) {
  const changes = []
  const origKeys = new Set(Object.keys(originalSections))
  const editKeys = new Set(Object.keys(editedSections))

  // 1. Section-level: added / removed
  for (const key of editKeys) {
    if (!origKeys.has(key)) {
      const editText = typeof editedSections[key] === 'object'
        ? editedSections[key].text || ''
        : String(editedSections[key] || '')
      changes.push({
        section: key,
        type: 'added',
        detail: 'Sección nueva agregada',
        original: '',
        edited: snippet(editText),
      })
    }
  }

  for (const key of origKeys) {
    if (!editKeys.has(key)) {
      const origVal = originalSections[key]
      const origText = typeof origVal === 'object' ? origVal.text || '' : String(origVal || '')
      changes.push({
        section: key,
        type: 'removed',
        detail: 'Sección eliminada',
        original: snippet(origText),
        edited: '',
      })
    }
  }

  // 2. Text diff within sections that exist in both
  for (const key of origKeys) {
    if (!editKeys.has(key)) continue

    const origText = normalize(
      typeof originalSections[key] === 'object'
        ? originalSections[key].text || ''
        : String(originalSections[key] || '')
    )
    const editText = normalize(
      typeof editedSections[key] === 'object'
        ? editedSections[key].text || ''
        : String(editedSections[key] || '')
    )

    if (origText === editText) continue

    const origLines = origText.split('\n').filter(l => l.trim())
    const editLines = editText.split('\n').filter(l => l.trim())

    const origSet = new Set(origLines.map(l => l.trim()))
    const editSet = new Set(editLines.map(l => l.trim()))

    const addedLines = editLines.filter(l => !origSet.has(l.trim()))
    const removedLines = origLines.filter(l => !editSet.has(l.trim()))

    // Check for modified lines (similar but not identical)
    const modifiedPairs = []
    const unmatchedAdded = []
    const unmatchedRemoved = []

    for (const removed of removedLines) {
      let bestMatch = null
      let bestScore = 0

      for (const added of addedLines) {
        if (modifiedPairs.some(p => p.edited === added)) continue
        const score = similarity(removed, added)
        if (score > bestScore && score >= 0.4) {
          bestScore = score
          bestMatch = added
        }
      }

      if (bestMatch) {
        modifiedPairs.push({ original: removed, edited: bestMatch })
      } else {
        unmatchedRemoved.push(removed)
      }
    }

    for (const added of addedLines) {
      if (!modifiedPairs.some(p => p.edited === added)) {
        unmatchedAdded.push(added)
      }
    }

    // Record changes
    for (const pair of modifiedPairs) {
      changes.push({
        section: key,
        type: 'modified',
        detail: detectModificationType(pair.original, pair.edited),
        original: snippet(pair.original),
        edited: snippet(pair.edited),
      })
    }

    for (const line of unmatchedAdded) {
      changes.push({
        section: key,
        type: 'added',
        detail: 'Línea agregada',
        original: '',
        edited: snippet(line),
      })
    }

    for (const line of unmatchedRemoved) {
      changes.push({
        section: key,
        type: 'removed',
        detail: 'Línea eliminada',
        original: snippet(line),
        edited: '',
      })
    }
  }

  const summary = buildSummary(changes)

  return {
    changes,
    summary,
    changesCount: changes.length,
  }
}

// ── STORE LEARNINGS ──────────────────────────────────────────────────────
// Persists learning data on the lead for future analysis

export function storeLearnings(lead, changes) {
  if (!lead || !changes?.changesCount) return

  if (!lead.formData) lead.formData = {}
  if (!lead.formData._learnings) lead.formData._learnings = []

  lead.formData._learnings.push({
    date: new Date().toISOString(),
    changesCount: changes.changesCount,
    changes: changes.changes,
    version: (lead.formData._learnings.length || 0) + 1,
  })
}

// ── GET LEARNING SUMMARY ─────────────────────────────────────────────────
// Returns a human-readable summary of all edits Sara has made on this lead

export function getLearningSummary(lead) {
  const learnings = lead?.formData?._learnings || []
  if (!learnings.length) {
    return { totalEdits: 0, sessions: 0, summary: 'Sin ediciones registradas.' }
  }

  const totalEdits = learnings.reduce((sum, l) => sum + l.changesCount, 0)
  const sessions = learnings.length

  // Count by type across all sessions
  const typeCounts = { modified: 0, added: 0, removed: 0 }
  const sectionEdits = {}

  for (const session of learnings) {
    for (const change of session.changes || []) {
      typeCounts[change.type] = (typeCounts[change.type] || 0) + 1
      sectionEdits[change.section] = (sectionEdits[change.section] || 0) + 1
    }
  }

  // Most edited sections
  const topSections = Object.entries(sectionEdits)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([section, count]) => `${friendlySectionName(section)} (${count})`)

  const parts = []
  if (typeCounts.modified) parts.push(`${typeCounts.modified} modificaciones`)
  if (typeCounts.added) parts.push(`${typeCounts.added} adiciones`)
  if (typeCounts.removed) parts.push(`${typeCounts.removed} eliminaciones`)

  let summary = `${totalEdits} cambios en ${sessions} sesion${sessions === 1 ? '' : 'es'}`
  if (parts.length) summary += `: ${parts.join(', ')}`
  if (topSections.length) summary += `. Secciones más editadas: ${topSections.join(', ')}`

  return { totalEdits, sessions, summary, typeCounts, sectionEdits }
}

// ── INTERNAL HELPERS ─────────────────────────────────────────────────────

function normalize(text) {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .trim()
    .toLowerCase()
}

function snippet(text, maxLen = 300) {
  if (!text) return ''
  const clean = text.trim()
  return clean.length > maxLen ? clean.substring(0, maxLen) + '...' : clean
}

// Simple similarity score (0-1) based on shared words
function similarity(a, b) {
  const wordsA = new Set(normalize(a).split(/\s+/))
  const wordsB = new Set(normalize(b).split(/\s+/))
  if (!wordsA.size || !wordsB.size) return 0

  let shared = 0
  for (const w of wordsA) {
    if (wordsB.has(w)) shared++
  }

  return (2 * shared) / (wordsA.size + wordsB.size)
}

function detectModificationType(original, edited) {
  const origLower = original.toLowerCase()
  const editLower = edited.toLowerCase()

  // Check if it's a reorder (same words, different order)
  const origWords = new Set(origLower.split(/\s+/))
  const editWords = new Set(editLower.split(/\s+/))
  const allSame = origWords.size === editWords.size && [...origWords].every(w => editWords.has(w))
  if (allSame && origWords.size > 2) return 'Reordenado'

  // Check if one is a substring of the other (expansion or trimming)
  if (editLower.includes(origLower) && editLower.length > origLower.length * 1.2) {
    return 'Texto expandido'
  }
  if (origLower.includes(editLower) && origLower.length > editLower.length * 1.2) {
    return 'Texto recortado'
  }

  return 'Texto modificado'
}

function buildSummary(changes) {
  if (!changes.length) return 'Sin cambios detectados.'

  // Group by section
  const bySec = {}
  for (const c of changes) {
    if (!bySec[c.section]) bySec[c.section] = { added: 0, removed: 0, modified: 0 }
    bySec[c.section][c.type]++
  }

  const parts = []
  for (const [section, counts] of Object.entries(bySec)) {
    const name = friendlySectionName(section)
    const actions = []
    if (counts.added) actions.push(`agregó ${counts.added} línea${counts.added > 1 ? 's' : ''}`)
    if (counts.modified) actions.push(`modificó ${counts.modified} comentario${counts.modified > 1 ? 's' : ''}`)
    if (counts.removed) actions.push(`eliminó ${counts.removed} elemento${counts.removed > 1 ? 's' : ''}`)
    if (actions.length) parts.push(`${actions.join(', ')} en ${name}`)
  }

  return `Sara ${parts.join('; ')}.`
}

function friendlySectionName(sectionId) {
  const names = {
    GREETING: 'Saludo',
    AGENDA: 'Agenda',
    LIGHTNING_LANE: 'Lightning Lane',
    DISNEY_SPRINGS: 'Disney Springs',
    CHECK_IN: 'Check-in',
    MEAL_PLAN: 'Dining Plan',
    INFO_ADICIONAL: 'Info Adicional',
    SHOPPING: 'Compras',
    EPIC_UNIVERSE: 'Epic Universe',
    CITY_WALK: 'CityWalk',
    PERSONALIZATION: 'Personalización',
    header: 'Encabezado',
  }

  // Handle dynamic sections like PARK_DAY:mk:1, PARK_RIDES:ep
  if (sectionId.startsWith('PARK_DAY:')) {
    const parts = sectionId.split(':')
    return `Día ${parts[2] || ''} - ${parkCodeToName(parts[1] || '')}`
  }
  if (sectionId.startsWith('PARK_RIDES:')) {
    return `Atracciones ${parkCodeToName(sectionId.split(':')[1] || '')}`
  }
  if (sectionId.startsWith('PARK_RESTAURANTS:')) {
    return `Restaurantes ${parkCodeToName(sectionId.split(':')[1] || '')}`
  }
  if (sectionId.startsWith('PARK_CHARACTERS:')) {
    return `Personajes ${parkCodeToName(sectionId.split(':')[1] || '')}`
  }
  if (sectionId.startsWith('PARK_SHOWS:')) {
    return `Shows ${parkCodeToName(sectionId.split(':')[1] || '')}`
  }

  return names[sectionId] || sectionId
}

function parkCodeToName(code) {
  const map = {
    mk: 'Magic Kingdom',
    ep: 'EPCOT',
    hs: 'Hollywood Studios',
    ak: 'Animal Kingdom',
    uso: 'Universal Studios',
    ioa: 'Islands of Adventure',
    epic: 'Epic Universe',
  }
  return map[code] || code.toUpperCase()
}

// Window bridge
Object.assign(window, { trackChanges, storeLearnings, getLearningSummary })
